/* jshint esversion: 6 */

/***********************************************************************
 An object of this class represents a SCORM-compatible API for storing
 SCO data to the TTKF publisher LMS database.
 ***********************************************************************/
import {RequestService} from "common/RequestService";
import {getLMSErrorMessage} from "publisher/LmsErrorCodes";
import {ErrorStack} from "publisher/ErrorStack";
import {Assert} from "common/Assert";

export function CourseToPublisherScormApi(serverURL, scoId, useCommitCache ) {
    "use strict";

    Assert.checkArgument(serverURL);
    Assert.checkArgumentType(scoId, "string");

    let commitCache = [];
    const errors = new ErrorStack("LMS error occurred");

    function requestScormCommand(query) {
        const response = RequestService.sendLmsRequest(query, null);

        if( response === "false" || response === "request_error" ) {
            errors.add(101);
        }

        if( response === "request_error" ) {
            throw new Error("Error on publisher request!");
        }

        return response;
    }

    function getLastErrorFromPublisher() {

        const query = `${serverURL}/get-last-error`;
        let result = requestScormCommand(query);

        if (result.length > 0) {
            result = Number(result);
            return isNaN(result) ? "0" : result.toString();
        }

        return "0";
    }

    function getErrorCodeFromParameter(parameter, defaultValue) {

        if (parameter.length === 0) {
            return defaultValue;
        }

        const errorCode = Number(parameter);
        if (isNaN(errorCode)) {
            return -1;
        }

        return errorCode;
    }

    // revealing public api
    return {
        /**
         * @returns {string}
         */
        LMSInitialize: () => requestScormCommand(`${serverURL}/initialize/${scoId}`),

        /**
         * @returns {string}
         */
        LMSFinish: () => {
            errors.clear();
            return requestScormCommand(`${serverURL}/terminate`);
        },

        /**
         * @param parameter
         * @returns {string}
         */
        LMSGetValue: (parameter) => {

            errors.clear();
            const query = `${serverURL}/get-value?key=${parameter}`;
            let result = requestScormCommand(query);

            if (result.length === 0) {
                return "";
            }

            while (result.substr(0, 1).search(/[\r\n]/) === 0) {
                result = result.substr(1, result.length - 1);
            }

            return decodeURIComponent(result);
        },

        /**
         * @param {String} key
         * @param {String} value
         * @returns {string} "true"/"false"
         */
        LMSSetValue: (key, value) => {
            Assert.checkArgument(key);

            const query = `${serverURL}/set-value?key=${key}&value=${encodeURIComponent(value)}`;
            if (useCommitCache) {
                commitCache.push(query);
                return "true";
            }

            errors.clear();
            return requestScormCommand(query);
        },

        /**
         * @returns {string}
         */
        LMSCommit: () => {

            if (!useCommitCache) {
                return "false";
            }

            errors.clear();
            commitCache.forEach((query) => {
                requestScormCommand(query);
            });
            commitCache = [];

            return errors.isEmpty() ? "true" : "false";
        },

        /**
         * @returns {string}
         */
        LMSGetLastError: function () {
            const errorCode = errors.getLast().code;
            return (errorCode !== 0) ? errorCode.toString() : getLastErrorFromPublisher();
        },

        /**
         * @param {String} errorNumber
         * @returns {String} message
         */
        LMSGetErrorString: function (errorNumber) {

            Assert.checkArgument(errorNumber);

            const errorCode = getErrorCodeFromParameter(errorNumber, errors.getLast().code);
            if (errorCode === -1) {
                return "Function parameter must be number as string!";
            }

            return getLMSErrorMessage(errorCode);
        },

        /**
         * @param {String} errorNumber
         * @returns {String} message
         */
        LMSGetDiagnostic: function (errorNumber) {

            Assert.checkArgument(errorNumber);

            const errorCode = getErrorCodeFromParameter(errorNumber, errors.getLast().code);
            if (errorCode === -1) {
                return "Function parameter must be empty or a number as string!";
            } else if (errorCode < 200) {
                return "";
            }

            return requestScormCommand(`${serverURL}/get-diagnostic?error=${errorCode.toString()}`);
        }
    };
}
